import { ChevronDown, ChevronRight } from "lucide-react";
import { useCallback } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Button } from "@/components/ui/button";
import {
	type ChartState,
	ChartTypes,
	Timeframes,
} from "@/components/widgets/Chart/types";
import { Chart } from "@/components/widgets/Chart/ui/Chart";
import { cn } from "@/lib/utils";
import { componentClassNames } from "../../../config";
import type { QuoteExpandedCharts } from "../../../type";
import { LineHeader } from "../LineHeader";

type QuoteChartProps = {
	ticker: string;
	chartType?: ChartTypes;
	timeFrame?: Timeframes;
	isCollapsible?: boolean;
	expandedCharts?: QuoteExpandedCharts;
	onStateChange?: OnStateChange;
};

export const QuoteChart = ({
	ticker,
	chartType = ChartTypes.LIGHT,
	timeFrame = Timeframes.ONE_DAY,
	isCollapsible = false,
	expandedCharts,
	onStateChange,
}: QuoteChartProps) => {
	const isExpanded = expandedCharts?.[timeFrame] ?? false;

	const toggleExpanded = useCallback(() => {
		const newExpandedState = !isExpanded;

		onStateChange?.({
			expandedCharts: {
				...expandedCharts,
				[timeFrame]: newExpandedState,
			},
		});
	}, [isExpanded, expandedCharts, timeFrame, onStateChange]);

	const state: ChartState = {
		ticker,
		type: chartType,
		isPartOfWidget: true,
		timeFrame,
		showFullRange: true,
	};
	const title =
		timeFrame === Timeframes.ONE_DAY
			? "Daily 1 Year"
			: timeFrame === Timeframes.FIVE_MIN
				? "5 min Intraday"
				: "";

	if (isCollapsible)
		return (
			<div className={cn(componentClassNames, "w-full h-full")}>
				<Button
					onClick={toggleExpanded}
					variant="dropdown"
					className="h-5 text-xs"
				>
					{title}

					{isExpanded ? (
						<ChevronDown className="w-4 h-4" />
					) : (
						<ChevronRight className="w-4 h-4" />
					)}
				</Button>

				{isExpanded && (
					<div className={cn("w-full h-[205px]")} data-testid="quote-chart">
						<Chart state={state} />
					</div>
				)}
			</div>
		);

	return (
		<div className={cn(componentClassNames, "w-full")}>
			<LineHeader title={title} />

			<div className={cn("flex-1 h-[205px]")} data-testid="quote-chart">
				<Chart state={state} />
			</div>
		</div>
	);
};
